"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useClickAway, useDebounce } from "react-use";
import type { CommonSelectItem } from "@/components/utils/select/CommonSelect";
import IdentitySearch, { IdentitySearchSize } from "@/components/utils/input/identity/IdentitySearch";
import type { ApiXTdhGrant } from "@/generated/models/ApiXTdhGrant";
import { ApiXTdhGrantStatus } from "@/generated/models/ApiXTdhGrantStatus";
import { useXtdhGrantsSearchQuery } from "@/hooks/useXtdhGrantsSearchQuery";
import GroupCreateXtdhGrantRow from "./subcomponents/GroupCreateXtdhGrantRow";
interface GroupCreateXtdhGrantModalProps {
  readonly isOpen: boolean;
  readonly selectedGrantId: string | null;
  readonly onClose: () => void;
  readonly onGrantSelect: (grant: ApiXTdhGrant) => void;
}
const STATUS_OPTIONS: readonly CommonSelectItem<ApiXTdhGrantStatus>[] = [{
  key: ApiXTdhGrantStatus.Granted,
  value: ApiXTdhGrantStatus.Granted,
  label: "Granted"
}, {
  key: ApiXTdhGrantStatus.Pending,
  value: ApiXTdhGrantStatus.Pending,
  label: "Pending"
}, {
  key: ApiXTdhGrantStatus.Disabled,
  value: ApiXTdhGrantStatus.Disabled,
  label: "Revoked"
}, {
  key: ApiXTdhGrantStatus.Failed,
  value: ApiXTdhGrantStatus.Failed,
  label: "Failed"
}];
export default function GroupCreateXtdhGrantModal(t0) {
  const $ = _c(83);
  const {
    isOpen,
    selectedGrantId,
    onClose,
    onGrantSelect
  } = t0;
  const modalRef = useRef(null);
  const skipInitialOutsideClick = useRef(true);
  const [selectedGrantor, setSelectedGrantor] = useState(null);
  const [targetCollectionInput, setTargetCollectionInput] = useState("");
  const [targetCollectionFilter, setTargetCollectionFilter] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(ApiXTdhGrantStatus.Granted);
  let t1;
  let t2;
  if ($[0] !== isOpen) {
    t1 = () => {
      if (isOpen) {
        skipInitialOutsideClick.current = true;
      }
      const timeout = globalThis.setTimeout(() => {
        skipInitialOutsideClick.current = false;
      }, 0);
      return () => {
        globalThis.clearTimeout(timeout);
      };
    };
    t2 = [isOpen];
    $[0] = isOpen;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== targetCollectionInput) {
    t3 = () => setTargetCollectionFilter(targetCollectionInput.trim());
    $[3] = targetCollectionInput;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== targetCollectionInput) {
    t4 = [targetCollectionInput];
    $[5] = targetCollectionInput;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  useDebounce(t3, 250, t4);
  let t5;
  if ($[7] !== onClose) {
    t5 = () => {
      if (skipInitialOutsideClick.current) {
        skipInitialOutsideClick.current = false;
        return;
      }
      onClose();
    };
    $[7] = onClose;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  useClickAway(modalRef, t5);
  let t6;
  let t7;
  if ($[9] !== isOpen || $[10] !== onClose) {
    t6 = () => {
      if (!isOpen) {
        return;
      }
      const handleEscape = event => {
        if (event.key === "Escape") {
          onClose();
        }
      };
      globalThis.addEventListener("keydown", handleEscape);
      return () => {
        globalThis.removeEventListener("keydown", handleEscape);
      };
    };
    t7 = [isOpen, onClose];
    $[9] = isOpen;
    $[10] = onClose;
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  useEffect(t6, t7);
  const t8 = targetCollectionFilter || null;
  let t9;
  if ($[13] !== selectedStatus) {
    t9 = [selectedStatus];
    $[13] = selectedStatus;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== isOpen || $[16] !== selectedGrantor || $[17] !== t8 || $[18] !== t9) {
    t10 = {
      grantor: selectedGrantor,
      targetCollectionName: t8,
      statuses: t9,
      enabled: isOpen,
      pageSize: 20
    };
    $[15] = isOpen;
    $[16] = selectedGrantor;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const {
    grants,
    totalCount,
    isLoading,
    isError,
    errorMessage,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useXtdhGrantsSearchQuery(t10);
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      setSelectedGrantor(null);
      setTargetCollectionInput("");
      setTargetCollectionFilter("");
      setSelectedStatus(ApiXTdhGrantStatus.Granted);
    };
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const onResetFilters = t11;
  if (!isOpen) {
    return null;
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="tw-fixed tw-inset-0 tw-bg-gray-600 tw-bg-opacity-50" />;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="tw-space-y-1"><p id="grant-modal-title" className="tw-m-0 tw-text-lg tw-font-semibold tw-text-iron-50">Find xTDH Grant</p><p className="tw-m-0 tw-text-sm tw-text-iron-400">Search global grants and select one for beneficiary filtering.</p></div>;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <span className="tw-sr-only">Close grant picker</span>;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <svg className="tw-h-5 tw-w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== onClose) {
    t16 = <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-border-b tw-border-solid tw-border-iron-800 tw-p-4 sm:tw-p-5">{t13}<button type="button" onClick={onClose} className="tw-rounded-full tw-border-0 tw-bg-transparent tw-p-2 tw-text-iron-400 tw-transition tw-duration-200 desktop-hover:hover:tw-text-iron-100">{t14}{t15}</button></div>;
    $[25] = onClose;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = identity => setSelectedGrantor(identity ? identity.toLowerCase() : null);
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== selectedGrantor) {
    t18 = <div className="tw-block"><IdentitySearch label="Grantor" size={IdentitySearchSize.SM} identity={selectedGrantor} setIdentity={t17} /></div>;
    $[28] = selectedGrantor;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = event_0 => setTargetCollectionInput(event_0.target.value);
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== targetCollectionInput) {
    t20 = <input type="text" value={targetCollectionInput} onChange={t19} placeholder="Collection name" aria-label="Collection name" className="tw-form-input tw-block tw-w-full tw-rounded-lg tw-border-0 tw-bg-iron-900 tw-px-3 tw-py-2.5 tw-text-sm tw-text-iron-50 tw-ring-1 tw-ring-inset tw-ring-iron-700 placeholder:tw-text-iron-500 focus:tw-ring-primary-400" />;
    $[31] = targetCollectionInput;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== t18 || $[34] !== t20) {
    t21 = <div className="tw-grid tw-grid-cols-1 tw-gap-3 lg:tw-grid-cols-2">{t18}{t20}</div>;
    $[33] = t18;
    $[34] = t20;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <span className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-iron-400">Status</span>;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== selectedStatus) {
    t23 = STATUS_OPTIONS.map(statusOption => {
      const isActive = selectedStatus === statusOption.value;
      return <button key={statusOption.key} type="button" onClick={() => setSelectedStatus(statusOption.value)} className={`tw-rounded-md tw-border tw-border-solid tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-transition tw-duration-200 ${isActive ? "tw-border-primary-400 tw-bg-primary-400/20 tw-text-primary-300" : "tw-border-iron-700 tw-bg-iron-900 tw-text-iron-300 desktop-hover:hover:tw-border-iron-600"}`}>{statusOption.label}</button>;
    });
    $[37] = selectedStatus;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  let t24;
  if ($[39] !== t23) {
    t24 = <div className="tw-min-w-0 tw-flex-1">{t22}<div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">{t23}</div></div>;
    $[39] = t23;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  let t25;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <button type="button" onClick={onResetFilters} className="tw-h-[42px] tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-text-sm tw-font-semibold tw-text-iron-300 tw-transition tw-duration-200 desktop-hover:hover:tw-bg-iron-800">Clear filters</button>;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  let t26;
  if ($[42] !== t24) {
    t26 = <div className="tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-3">{t24}{t25}</div>;
    $[42] = t24;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  let t27;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <p className="tw-m-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-iron-400">Results</p>;
    $[44] = t27;
  } else {
    t27 = $[44];
  }
  let t28;
  if ($[45] !== totalCount) {
    t28 = <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-solid tw-border-iron-800 tw-px-4 tw-py-2.5">{t27}<p className="tw-m-0 tw-text-xs tw-text-iron-500">{totalCount} total</p></div>;
    $[45] = totalCount;
    $[46] = t28;
  } else {
    t28 = $[46];
  }
  let t29;
  if ($[47] !== grants.length || $[48] !== isLoading) {
    t29 = isLoading && !grants.length && <p className="tw-m-0 tw-rounded-lg tw-bg-iron-900 tw-p-4 tw-text-sm tw-text-iron-400">Loading grants...</p>;
    $[47] = grants.length;
    $[48] = isLoading;
    $[49] = t29;
  } else {
    t29 = $[49];
  }
  let t30;
  if ($[50] !== errorMessage || $[51] !== grants.length || $[52] !== isError || $[53] !== refetch) {
    t30 = isError && !grants.length && <div className="tw-rounded-lg tw-border tw-border-solid tw-border-red/30 tw-bg-red/10 tw-p-4"><p className="tw-m-0 tw-text-sm tw-text-red">{errorMessage ?? "Unable to load grants."}</p><button type="button" onClick={() => {
        refetch().catch(_temp);
      }} className="tw-mt-3 tw-rounded-md tw-border tw-border-solid tw-border-red/40 tw-bg-red/20 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-red">Retry</button></div>;
    $[50] = errorMessage;
    $[51] = grants.length;
    $[52] = isError;
    $[53] = refetch;
    $[54] = t30;
  } else {
    t30 = $[54];
  }
  let t31;
  if ($[55] !== grants.length || $[56] !== isError || $[57] !== isLoading) {
    t31 = !isLoading && !isError && !grants.length && <p className="tw-m-0 tw-rounded-lg tw-bg-iron-900 tw-p-4 tw-text-sm tw-text-iron-400">No grants matched the selected filters.</p>;
    $[55] = grants.length;
    $[56] = isError;
    $[57] = isLoading;
    $[58] = t31;
  } else {
    t31 = $[58];
  }
  let t32;
  if ($[59] !== grants || $[60] !== onGrantSelect || $[61] !== selectedGrantId) {
    t32 = !!grants.length && <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">{grants.map(grant => <GroupCreateXtdhGrantRow key={grant.id} grant={grant} isSelected={selectedGrantId === grant.id} interactive={true} asListItem={true} onSelect={onGrantSelect} />)}</ul>;
    $[59] = grants;
    $[60] = onGrantSelect;
    $[61] = selectedGrantId;
    $[62] = t32;
  } else {
    t32 = $[62];
  }
  let t33;
  if ($[63] !== t29 || $[64] !== t30 || $[65] !== t31 || $[66] !== t32) {
    t33 = <div className="tw-max-h-[360px] tw-overflow-y-auto tw-p-2 sm:tw-p-3">{t29}{t30}{t31}{t32}</div>;
    $[63] = t29;
    $[64] = t30;
    $[65] = t31;
    $[66] = t32;
    $[67] = t33;
  } else {
    t33 = $[67];
  }
  let t34;
  if ($[68] !== fetchNextPage || $[69] !== hasNextPage || $[70] !== isFetchingNextPage) {
    t34 = hasNextPage && <div className="tw-border-t tw-border-solid tw-border-iron-800 tw-p-3"><button type="button" onClick={() => fetchNextPage()} disabled={isFetchingNextPage} className="tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-iron-300 tw-transition tw-duration-200 disabled:tw-cursor-not-allowed disabled:tw-opacity-60 desktop-hover:hover:tw-bg-iron-800">{isFetchingNextPage ? "Loading..." : "Load more"}</button></div>;
    $[68] = fetchNextPage;
    $[69] = hasNextPage;
    $[70] = isFetchingNextPage;
    $[71] = t34;
  } else {
    t34 = $[71];
  }
  let t35;
  if ($[72] !== t28 || $[73] !== t33 || $[74] !== t34) {
    t35 = <div className="tw-rounded-lg tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-900/40">{t28}{t33}{t34}</div>;
    $[72] = t28;
    $[73] = t33;
    $[74] = t34;
    $[75] = t35;
  } else {
    t35 = $[75];
  }
  let t36;
  if ($[76] !== t21 || $[77] !== t26 || $[78] !== t35) {
    t36 = <div className="tw-space-y-4 tw-p-4 sm:tw-p-5">{t21}{t26}{t35}</div>;
    $[76] = t21;
    $[77] = t26;
    $[78] = t35;
    $[79] = t36;
  } else {
    t36 = $[79];
  }
  let t37;
  if ($[80] !== t16 || $[81] !== t36) {
    t37 = createPortal(<div className="tailwind-scope tw-relative tw-z-1000">{t12}<div className="tw-fixed tw-inset-0 tw-z-10 tw-overflow-y-auto"><div className="tw-flex tw-min-h-full tw-items-end tw-justify-center tw-p-2 tw-text-center sm:tw-items-center lg:tw-p-4"><div ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="grant-modal-title" className="tw-relative tw-w-full tw-transform tw-rounded-xl tw-bg-iron-950 tw-text-left tw-shadow-xl tw-transition-all tw-duration-500 sm:tw-max-w-4xl">{t16}{t36}</div></div></div></div>, document.body);
    $[80] = t16;
    $[81] = t36;
    $[82] = t37;
  } else {
    t37 = $[82];
  }
  return t37;
}
function _temp() {}